import AddIcon from "@mui/icons-material/Add";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Link, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type { Paginated, Person } from "../api/types";
import { useAuth } from "../auth/AuthContext";

export function PeoplePage() {
  const { can } = useAuth();
  const [szukaj, setSzukaj] = useState("");
  const [otwarty, setOtwarty] = useState(false);
  const widziPii = can("contractor.view_pii");

  const { data } = useQuery({
    queryKey: ["people", szukaj],
    queryFn: () => api.get<Paginated<Person>>(
      `/people${szukaj ? `?q=${encodeURIComponent(szukaj)}` : ""}`),
  });

  const osoby = data?.results ?? [];

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h6">Kartoteka wykonawców</Typography>
        <TextField placeholder="Nazwisko" value={szukaj} sx={{ width: 240 }}
                   onChange={(e) => setSzukaj(e.target.value)} />
        <Box sx={{ flex: 1 }} />
        {can("contractor.edit") && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOtwarty(true)}>
            Dodaj wykonawcę
          </Button>
        )}
      </Stack>

      {!widziPii && (
        <Alert severity="info">
          Numery PESEL, telefony i rachunki są ukryte — nie są nawet pobierane
          z bazy dla Twoich uprawnień.
        </Alert>
      )}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nazwisko i imię</TableCell>
              {widziPii && <TableCell>PESEL</TableCell>}
              {widziPii && <TableCell>Telefon</TableCell>}
              <TableCell>Dane potwierdzone</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {osoby.map((o) => (
              <TableRow key={o.id} hover>
                <TableCell>
                  {can("contractor.edit") ? (
                    <Link component={RouterLink} to={`/wykonawcy/${o.id}`}>
                      {o.last_name} {o.first_name}
                    </Link>
                  ) : `${o.last_name} ${o.first_name}`}
                </TableCell>
                {widziPii && <TableCell>{o.pesel ?? "—"}</TableCell>}
                {widziPii && <TableCell>{o.phone || "—"}</TableCell>}
                <TableCell>
                  {/* Okno prefillu to 3 lata (CLAUDE.md §4.1) — po nim
                      wykonawca przechodzi pełny kreator od nowa. */}
                  <Chip size="small"
                        color={o.prefill_valid ? "success" : "warning"}
                        label={o.prefill_valid
                          ? `potwierdzone ${o.last_confirmed_at?.slice(0, 10)}`
                          : "wymaga potwierdzenia"} />
                </TableCell>
              </TableRow>
            ))}
            {osoby.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {szukaj ? "Nikogo nie znaleziono." : "Kartoteka jest pusta."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <NowyWykonawca open={otwarty} onClose={() => setOtwarty(false)} />
    </Stack>
  );
}

function NowyWykonawca({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", pesel: "", phone: "" });
  const [blad, setBlad] = useState<ApiError | null>(null);
  const kolejka = useQueryClient();

  const zapisz = useMutation({
    mutationFn: () => api.post<Person>("/people", form),
    onSuccess: () => {
      kolejka.invalidateQueries({ queryKey: ["people"] });
      setForm({ first_name: "", last_name: "", pesel: "", phone: "" });
      setBlad(null);
      onClose();
    },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  const pole = (name: keyof typeof form) => ({
    value: form[name],
    error: Boolean(blad?.field(name)),
    helperText: blad?.field(name),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [name]: e.target.value }),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Dodaj wykonawcę do kartoteki</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {blad && !Object.keys(blad.body.fields).length && (
            <Alert severity={blad.body.code === "duplicate" ? "warning" : "error"}>
              {blad.message}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            Kartoteka jest trwała — ci sami ludzie wracają produkcja po produkcji.
            Numer PESEL i telefon leżą w bazie zaszyfrowane.
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField label="Imię" {...pole("first_name")} sx={{ flex: 1 }} />
            <TextField label="Nazwisko" {...pole("last_name")} sx={{ flex: 1 }} />
          </Stack>
          <TextField label="PESEL" {...pole("pesel")}
                     helperText={blad?.field("pesel") ?? "Sprawdzamy sumę kontrolną."} />
          <TextField label="Telefon" {...pole("phone")}
                     helperText={blad?.field("phone")
                       ?? "Na ten numer idzie kod dostępu do portalu."} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button variant="contained" onClick={() => zapisz.mutate()}
                disabled={zapisz.isPending}>Dodaj</Button>
      </DialogActions>
    </Dialog>
  );
}
